import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { RefreshCw } from 'lucide-react';
import { CHART_SERIES } from '../../config/statusColors';
import { formatCurrency } from '../../lib/fixedIncome';
import { CASH_FLOW_SCHEDULE } from '../../data/portfolioMockData';
import ExportToolbar from './ExportToolbar';
import { exportToExcel, exportToPdf, CASHFLOW_EXPORT_COLUMNS } from '../../lib/exportUtils';

const CashFlowsTab = ({ positions, role, cashFlows: cashFlowsProp }) => {
  const cashFlowData = cashFlowsProp || CASH_FLOW_SCHEDULE;

  // Filter cash flows based on role
  const filteredCashFlows = useMemo(() => {
    if (role === 'investor_analyst') {
      const ent003PositionIds = new Set(
        (positions || [])
          .filter((p) => p.entity === 'ENT-003')
          .map((p) => p.id)
      );
      return cashFlowData.filter((cf) => ent003PositionIds.has(cf.positionId));
    }
    return cashFlowData;
  }, [positions, role, cashFlowData]);

  // Build monthlyData for stacked bar chart
  const { monthlyData, countries } = useMemo(() => {
    const monthMap = {};
    const countrySet = new Set();

    filteredCashFlows.forEach((cf) => {
      const d = new Date(cf.date);
      const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });

      if (!monthMap[key]) {
        monthMap[key] = { month: label, _sortKey: key };
      }
      monthMap[key][cf.country] = (monthMap[key][cf.country] || 0) + cf.amountUsd;
      countrySet.add(cf.country);
    });

    const sorted = Object.values(monthMap).sort((a, b) =>
      a._sortKey.localeCompare(b._sortKey)
    );
    sorted.forEach((m) => delete m._sortKey);

    return { monthlyData: sorted, countries: Array.from(countrySet) };
  }, [filteredCashFlows]);

  // Build tableData with running cumulative total
  const tableData = useMemo(() => {
    const sorted = [...filteredCashFlows].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    let cumulative = 0;
    return sorted.map((cf) => {
      cumulative += cf.amountUsd;
      return { ...cf, cumulative };
    });
  }, [filteredCashFlows]);

  // Summary stats
  const totalUpcoming = filteredCashFlows.reduce((s, cf) => s + cf.amountUsd, 0);
  const nextMaturityDate =
    tableData.length > 0
      ? new Date(tableData[0].date).toLocaleDateString()
      : '—';
  const autoRollCount = filteredCashFlows.filter((cf) => cf.autoRoll).length;

  // YAxis tick formatter
  const yAxisFormatter = (value) => {
    if (Math.abs(value) >= 1e6) return '$' + (value / 1e6).toFixed(1) + 'M';
    if (Math.abs(value) >= 1e3) return '$' + (value / 1e3).toFixed(0) + 'K';
    return '$' + value;
  };

  // Currency symbol helper
  const currencySymbols = {
    KES: 'KSh', NGN: '₦', GHS: 'GH₵', RWF: 'FRw', UGX: 'USh',
    TZS: 'TSh', ZAR: 'R', EGP: 'E£', USD: '$',
  };

  const formatLocal = (amount, currency) => {
    const sym = currencySymbols[currency] || currency + ' ';
    return sym + amount.toLocaleString();
  };

  const handleExportExcel = () => {
    exportToExcel(filteredCashFlows, CASHFLOW_EXPORT_COLUMNS, 'Cash Flows', 'portfolio-cash-flows');
  };

  return (
    <div className="space-y-6">
      {/* Export Toolbar */}
      <div className="flex justify-end">
        <ExportToolbar onExportExcel={handleExportExcel} onExportPdf={exportToPdf} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
            Total Upcoming
          </p>
          <p className="text-2xl font-bold font-sans text-black mt-1">
            {formatCurrency(totalUpcoming)}
          </p>
        </div>

        <div className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
            Next Maturity
          </p>
          <p className="text-2xl font-bold font-sans text-black mt-1">
            {nextMaturityDate}
          </p>
        </div>

        <div className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
            Auto-Roll Eligible
          </p>
          <p className="text-2xl font-bold font-sans text-black mt-1">
            {autoRollCount}
          </p>
        </div>
      </div>

      {/* Stacked Bar Chart */}
      <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Monthly Cash Flow Projection
        </h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={yAxisFormatter} />
            <Tooltip
              formatter={(value) => formatCurrency(value)}
            />
            <Legend />
            {countries.map((country, idx) => (
              <Bar
                key={country}
                dataKey={country}
                stackId="a"
                fill={CHART_SERIES[idx % CHART_SERIES.length]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cash Flow Table */}
      <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Cash Flow Schedule
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instrument</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount (Local)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount (USD)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cumulative (USD)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Auto-Roll</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tableData.map((cf, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {new Date(cf.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {cf.type === 'Maturity' ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: '#BDD6FF', color: '#2B5EA7' }}
                      >
                        {cf.type}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: '#CDF3FC', color: '#1A7A8A' }}
                      >
                        {cf.type}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {cf.positionId}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {cf.instrument}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {cf.country}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatLocal(cf.amountLocal, cf.currency)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatCurrency(cf.amountUsd)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap font-medium">
                    {formatCurrency(cf.cumulative)}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {cf.autoRoll ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: '#CDF3FC', color: '#1A7A8A' }}
                      >
                        <RefreshCw size={12} />
                        Auto-roll
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                      >
                        Manual
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CashFlowsTab;
